export interface QualityBadge {
  badge: string;
  label: string;
  color: 'green' | 'blue' | 'yellow' | 'red' | 'gray';
}

export interface InspirationQualityStats {
  inspiration_source_id: string;
  username: string;
  total_scraped: number;
  total_excluded: number;
  total_generated: number;
  sponsored_excluded: number;
  yield_rate: number;
  avg_viral_score: number | null;
  ideas_count: number;
}

export function getInspirationQuality(
  yieldRate: number,
  avgViralScore: number | null,
  totalScraped: number,
  ideasCount: number
): QualityBadge {
  if (totalScraped < 5 || ideasCount < 3) {
    return { badge: '\u23F3', label: 'Za mało danych', color: 'gray' };
  }

  const score = avgViralScore ?? 0;
  const highYield = yieldRate >= 60;
  const highScore = score >= 65;

  if (highYield && highScore) {
    return { badge: '\uD83C\uDFC6', label: 'Złota inspiracja', color: 'green' };
  }
  if (!highYield && highScore) {
    return { badge: '\uD83D\uDC8E', label: 'Rzadkie perełki', color: 'blue' };
  }
  if (highYield && !highScore) {
    return { badge: '\u26A0\uFE0F', label: 'Ilość bez jakości', color: 'yellow' };
  }
  return { badge: '\u274C', label: 'Rozważ zmianę', color: 'red' };
}

export function getInspirationInsight(stats: InspirationQualityStats): string {
  const { total_scraped, yield_rate, avg_viral_score, ideas_count } = stats;

  if (total_scraped < 5 || ideas_count < 3) {
    return 'Czekamy na więcej postów.';
  }

  const score = avg_viral_score ?? 0;
  const highYield = yield_rate >= 60;
  const highScore = score >= 65;

  if (highYield && highScore) {
    return 'Strzał w dziesiątkę. Trzymaj.';
  }
  if (!highYield && highScore) {
    return 'Rzadko, ale mocno. Warto obserwować.';
  }
  if (highYield && !highScore) {
    return 'Dużo postów, mało złota. Rozważ zmianę.';
  }
  return 'Słabe wyniki. Zamień na coś lepszego.';
}

export function getViralScoreColor(score: number): string {
  if (score <= 40) return '#ef4444';
  if (score <= 60) {
    const t = (score - 40) / 20;
    return lerpColor('#ef4444', '#f97316', t);
  }
  if (score <= 75) {
    const t = (score - 60) / 15;
    return lerpColor('#f97316', '#f59e0b', t);
  }
  const t = Math.min((score - 75) / 25, 1);
  return lerpColor('#f59e0b', '#10b981', t);
}

function lerpColor(a: string, b: string, t: number): string {
  const parse = (hex: string) => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
  const [r1, g1, b1] = parse(a);
  const [r2, g2, b2] = parse(b);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const bl = Math.round(b1 + (b2 - b1) * t);
  return `rgb(${r}, ${g}, ${bl})`;
}

const BADGE_COLORS: Record<QualityBadge['color'], { bg: string; text: string; border: string }> = {
  green: { bg: 'bg-green-500/10', text: 'text-green-600', border: 'border-green-500/20' },
  blue: { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500/20' },
  yellow: { bg: 'bg-yellow-500/10', text: 'text-yellow-600', border: 'border-yellow-500/20' },
  red: { bg: 'bg-red-500/10', text: 'text-red-600', border: 'border-red-500/20' },
  gray: { bg: 'bg-muted/50', text: 'text-muted-foreground', border: 'border-border' },
};

export function getBadgeColors(color: QualityBadge['color']) {
  return BADGE_COLORS[color];
}
